import {
	IconAffiliate,
	IconAppWindow,
	IconArrowUpRight,
	IconArticle,
	IconChartHistogram,
	IconDiscount,
	IconExclamationMark,
	IconHome,
	IconIdBadge2,
	IconLogout,
	IconMenu2,
	IconReceiptDollar,
	IconSchool,
	IconSocial,
	IconSparkles,
	IconX,
} from "@tabler/icons-react"
import { type ComponentType, type FC, memo, type RefObject, useEffect, useRef } from "react"
import { useLocation } from "react-router"

import { getAiCreditBalanceStatus } from "@forento/core/models/ai"
import { toNumberFormat } from "@forento/core/utilities/number"
import { cn } from "@forento/ui/utilities/tailwind"

import {
	BundleIcon,
	CalendarIcon,
	CourseIcon,
	DownloadIcon,
	ExamIcon,
	SessionsIcon,
	SettingsIcon,
	UsersIcon,
	VideoIcon,
} from "~/components/Icon"
import CustomLink from "~/components/Link"
import { useBrand } from "~/contexts/BrandContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import useMediaQuery from "~/hooks/useMediaQuery"
import routes from "~/utilities/routes"
import { query } from "~/utilities/trpc"

import WorkspaceIdentity from "./WorkspaceIdentity"

interface SidebarSection {
	title: string
	items: SectionItem[]
}
interface SectionItem {
	label: string
	Icon: ComponentType<{ className?: string }>
	onClick: string | (() => void)
	activeUrl?: string
	exactActive?: boolean
	newFeature?: boolean
	hasImportantNotice?: boolean
	notificationCount?: number
}

function getCollapsedTooltipProps(isCollapsed: boolean, label: string) {
	if (!isCollapsed) return null

	return { "data-tooltip-id": "tooltip", "data-tooltip-content": label, "data-tooltip-place": "right" } as const
}

type SidebarProps = {
	id: string
	isDrawerOpen: boolean
	setDrawerOpen(value: boolean | ((current: boolean) => boolean)): void
	/** The phone top bar's menu button, which takes focus back when the drawer closes there. */
	externalToggleRef: RefObject<HTMLButtonElement | null>
}

const Sidebar: FC<SidebarProps> = ({ id: sidebarId, isDrawerOpen, setDrawerOpen, externalToggleRef }) => {
	const brand = useBrand()
	const user = useUser()
	const platform = usePlatform()!
	const location = useLocation()
	const utilities = query.useUtils()
	const isNarrowScreen = useMediaQuery("(width < 64rem)")
	const isPhone = useMediaQuery("(width < 40rem)")
	// Below 64rem the sidebar is an icon rail that can be expanded into a labelled drawer over the page.
	// Phones have no rail, so the drawer keeps its labelled layout while it slides out of view.
	const isExpanded = !isNarrowScreen || isPhone || isDrawerOpen
	const isCollapsed = !isExpanded
	const aiCreditBalanceStatus = getAiCreditBalanceStatus(platform.aiCreditBalance)
	const aiCreditBalanceLabel =
		aiCreditBalanceStatus === "depleted"
			? " (out of credits)"
			: aiCreditBalanceStatus === "low"
				? " (running low)"
				: ""
	const canShowCommunityNotifications = platform.community !== null && user.user!.communityProfileId !== null
	const notificationCounts = query.community.getCreatorNotificationCounts.useQuery(undefined, {
		enabled: canShowCommunityNotifications,
		refetchInterval: 30_000,
	})
	const { mutate: markNotificationsRead } = query.community.markCreatorNotificationsRead.useMutation({
		async onSuccess(_, type) {
			utilities.community.getCreatorNotificationCounts.setData(undefined, counts =>
				counts === undefined ? counts : { ...counts, [type]: 0 },
			)
			await utilities.community.getCreatorNotificationCounts.invalidate()
		},
	})

	useEffect(() => {
		if (!canShowCommunityNotifications) return

		const type =
			location.pathname === routes.community.posts()
				? "posts"
				: location.pathname.startsWith(routes.community.messages())
					? "messages"
					: null

		if (type !== null) markNotificationsRead(type)
	}, [canShowCommunityNotifications, location.pathname, markNotificationsRead])

	// Close the drawer after navigating and when the screen grows past the breakpoint.
	useEffect(() => {
		setDrawerOpen(false)
	}, [location.pathname, isNarrowScreen, setDrawerOpen])

	// The open drawer behaves as a modal: focus moves into it, Tab stays inside, and closing returns focus to the toggle.
	const asideRef = useRef<HTMLElement>(null)
	const toggleRef = useRef<HTMLButtonElement>(null)
	useEffect(() => {
		if (!isDrawerOpen) return
		const aside = asideRef.current
		if (aside === null) return
		const getFocusable = () =>
			[...aside.querySelectorAll<HTMLElement>("a[href], button:not([disabled])")].filter(
				element => element.getClientRects().length > 0,
			)
		getFocusable()[0]?.focus()

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				if (event.defaultPrevented || event.isComposing) return
				setDrawerOpen(false)
				return
			}
			if (event.key !== "Tab") return
			const focusable = getFocusable()
			const first = focusable[0]
			const last = focusable.at(-1)
			if (first === undefined || last === undefined) return
			const active = document.activeElement
			if (event.shiftKey && (active === first || !aside.contains(active))) {
				event.preventDefault()
				last.focus()
			} else if (!event.shiftKey && (active === last || !aside.contains(active))) {
				event.preventDefault()
				first.focus()
			}
		}
		document.addEventListener("keydown", handleKeyDown)
		return () => {
			document.removeEventListener("keydown", handleKeyDown)
			// Navigating moves focus to the new page, so only hand focus back when it is still in the drawer.
			if (aside.contains(document.activeElement) || document.activeElement === document.body) {
				// The phone top bar's button comes first: the closed phone drawer keeps its layout, so its own toggle still has boxes.
				const toggle = [externalToggleRef.current, toggleRef.current].find(
					button => button !== null && button.getClientRects().length > 0,
				)
				toggle?.focus()
			}
		}
	}, [isDrawerOpen, setDrawerOpen, externalToggleRef])

	const communityNotificationCount =
		(notificationCounts.data?.posts ?? 0) +
		(notificationCounts.data?.messages ?? 0) +
		(notificationCounts.data?.moderation ?? 0)

	const sections: SidebarSection[] = [
		{
			title: "Content",
			items: [
				{ label: "Dashboard", Icon: IconHome, onClick: routes.main.index(), exactActive: true },
				{
					label: "Website editor",
					Icon: IconAppWindow,
					onClick: routes.main.websiteEditor(),
					exactActive: true,
				},
				{
					label: "Blog",
					Icon: IconArticle,
					onClick: routes.main.websiteBlog(),
					newFeature: true,
				},
				{ label: "Courses", Icon: CourseIcon, onClick: routes.course.index() },
				...(platform.content.legacyExams
					? [{ label: "Exams", Icon: ExamIcon, onClick: routes.exam.index(), hasImportantNotice: true }]
					: []),
				{ label: "Videos", Icon: VideoIcon, onClick: routes.video.index() },
				{ label: "Downloadables", Icon: DownloadIcon, onClick: routes.downloadable.index() },
				{ label: "Bundles", Icon: BundleIcon, onClick: routes.bundle.index() },
				{ label: "Membership", Icon: IconIdBadge2, onClick: routes.membership.index() },
			],
		},
	]

	sections.push({
		title: "Community & events",
		items: [
			{
				label: "Community",
				Icon: IconSocial,
				onClick: platform.community !== null ? routes.community.posts() : routes.community.index(),
				activeUrl: routes.community.index(),
				notificationCount: communityNotificationCount,
			},
			{ label: "Sessions", Icon: SessionsIcon, onClick: routes.sessions.index() },
			{ label: "Events", Icon: CalendarIcon, onClick: routes.main.event() },
		],
	})

	sections.push({
		title: "Business",
		items: [
			{ label: "Users", Icon: UsersIcon, onClick: routes.user.index() },
			{ label: "Analytics", Icon: IconChartHistogram, onClick: routes.main.analytics() },
			{
				label: "Payments",
				Icon: IconReceiptDollar,
				onClick: routes.payments.index(),
				activeUrl: routes.payments.index(),
			},
			{ label: "Discount codes", Icon: IconDiscount, onClick: routes.main.discountCodes() },
			{ label: "Referrals", Icon: IconAffiliate, onClick: routes.main.referral() },
		],
	})
	const accountItems: SectionItem[] = [
		{ label: "Settings", Icon: SettingsIcon, onClick: routes.settings.index() },
		{ label: "Sign out", Icon: IconLogout, onClick: user.signOut },
	]

	return (
		// On phones the rail is hidden entirely and the drawer opens from the top bar's menu button.
		// Phones keep the stacking order while closed so the drawer stays above the top bar as it slides away.
		<div
			className={cn(
				"sticky top-0 h-dvh w-16 shrink-0 self-start max-sm:z-50 max-sm:w-0 lg:w-60",
				isDrawerOpen && "z-50",
			)}
		>
			<div
				aria-hidden="true"
				className={cn(
					"fixed inset-0 z-40 bg-black/30 motion-safe:transition-[opacity,visibility] motion-safe:duration-300 lg:hidden",
					!isDrawerOpen && "invisible opacity-0",
				)}
				onClick={() => setDrawerOpen(false)}
			/>
			<aside
				ref={asideRef}
				id={sidebarId}
				// The closed phone drawer stays visible while it slides away, so it leaves the tab order at once.
				inert={isPhone && !isDrawerOpen}
				aria-label="Creator workspace"
				// While open on a narrow screen the drawer is modal: the page behind it is inert.
				{...(isDrawerOpen ? { role: "dialog", "aria-modal": true } : {})}
				data-sidebar-expanded={isExpanded}
				className={cn(
					"border-component-border bg-component-background flex h-dvh w-full flex-col border-r",
					// On phones the drawer slides in from the left edge; it stays invisible while closed so it is out of the tab order.
					"max-sm:fixed max-sm:inset-y-0 max-sm:left-0 max-sm:z-50 max-sm:w-60 max-sm:shadow-xl max-sm:motion-safe:duration-300 max-sm:motion-safe:ease-[cubic-bezier(0.32,0.72,0,1)]",
					// Opening shows the drawer at once so focus can move into it; closing keeps it visible until it has slid away.
					isDrawerOpen
						? "fixed inset-y-0 left-0 z-50 w-60 shadow-xl max-sm:motion-safe:transition-[translate]"
						: "max-sm:invisible max-sm:-translate-x-full max-sm:motion-safe:transition-[translate,visibility]",
				)}
			>
				<div className="expanded:justify-between expanded:px-6 flex h-20 shrink-0 items-center justify-center gap-2 px-2">
					<CustomLink
						to={routes.main.index()}
						className={cn(
							"focus-visible:outline-primary rounded-md focus-visible:outline-2 focus-visible:outline-offset-4",
							isCollapsed && "hidden",
						)}
					>
						<img
							alt={`${brand.name}'s logo`}
							src={brand.logoUrl}
							className="expanded:h-7 expanded:w-28 size-8 object-contain"
						/>
					</CustomLink>
					{isNarrowScreen && (
						<button
							ref={toggleRef}
							type="button"
							onClick={() => setDrawerOpen(current => !current)}
							aria-expanded={isDrawerOpen}
							aria-controls={sidebarId}
							aria-label={isDrawerOpen ? "Close navigation" : "Open navigation"}
							className="focus-visible:outline-primary flex size-10 cursor-pointer items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-black/4 hover:text-slate-950 focus-visible:outline-2 focus-visible:outline-offset-2"
						>
							{isExpanded ? <IconX className="size-5" /> : <IconMenu2 className="size-5" />}
						</button>
					)}
				</div>

				<div className="border-component-border expanded:mx-3 expanded:p-1.5 mx-2 mb-3 shrink-0 rounded-xl border bg-white p-1">
					<WorkspaceIdentity className="border-component-border expanded:flex mx-1.5 mt-1 mb-1.5 hidden border-b pt-1 pb-3" />
					{platform.domain.landingPage && (
						<a
							href={`//${platform.domain.landingPage}`}
							target="_blank"
							rel="noreferrer noopener"
							className="group hover:bg-component-background focus-visible:outline-primary expanded:justify-start flex min-h-9 items-center justify-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2"
							aria-label="My website (opens in a new tab)"
							{...getCollapsedTooltipProps(isCollapsed, "My website")}
						>
							<IconAppWindow className="size-4 shrink-0" />
							<span className="expanded:block hidden flex-1 truncate">My website</span>
							<IconArrowUpRight className="group-hover:text-primary expanded:block hidden size-3.5 text-slate-400 transition-colors" />
						</a>
					)}
					<a
						href={`//${platform.domain.platform}`}
						target="_blank"
						rel="noreferrer noopener"
						className="group hover:bg-component-background focus-visible:outline-primary expanded:justify-start flex min-h-9 items-center justify-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2"
						aria-label="My academy (opens in a new tab)"
						{...getCollapsedTooltipProps(isCollapsed, "My academy")}
					>
						<IconSchool className="size-4 shrink-0" />
						<span className="expanded:block hidden flex-1 truncate">My academy</span>
						<IconArrowUpRight className="group-hover:text-primary expanded:block hidden size-3.5 text-slate-400 transition-colors" />
					</a>
					{platform.enabledFeatures.includes("ai-generated-courses") && (
						<CustomLink
							to={routes.settings.billing.plans()}
							className="group hover:bg-component-background focus-visible:outline-primary expanded:justify-start flex min-h-9 items-center justify-center gap-2.5 rounded-lg px-2 py-2 text-sm font-medium text-slate-600 transition-colors duration-150 hover:text-slate-900 focus-visible:outline-2 focus-visible:outline-offset-2"
							aria-label={`Forento AI: ${toNumberFormat(platform.aiCreditBalance)} credits${aiCreditBalanceLabel}`}
							{...getCollapsedTooltipProps(
								isCollapsed,
								`Forento AI · ${toNumberFormat(platform.aiCreditBalance)} credits${aiCreditBalanceLabel}`,
							)}
						>
							<IconSparkles className="text-primary size-4 shrink-0" />
							<span className="expanded:block hidden flex-1 truncate">Forento AI</span>
							<span
								className={cn(
									"expanded:block hidden shrink-0 rounded-md px-1.5 py-0.5 text-[11px] leading-4 font-semibold tabular-nums",
									aiCreditBalanceStatus === "depleted"
										? "bg-red-100 text-red-700"
										: aiCreditBalanceStatus === "low"
											? "bg-amber-100 text-amber-800"
											: "bg-primary/8 text-primary",
								)}
							>
								{toNumberFormat(platform.aiCreditBalance)}
							</span>
						</CustomLink>
					)}
				</div>

				<nav
					aria-label="Main navigation"
					className="expanded:px-3 min-h-0 flex-1 [scrollbar-width:thin] overflow-y-auto overscroll-contain px-2 pt-1 pb-4"
				>
					<div className="flex flex-col gap-5">
						{sections.map(section => (
							<section
								key={section.title}
								aria-label={section.title}
								className="border-component-border expanded:border-0 expanded:pt-0 border-t pt-4 first:border-0 first:pt-0"
							>
								<div className="expanded:block mb-2 hidden px-3 text-[10px] font-semibold tracking-[0.14em] text-slate-500 uppercase">
									{section.title}
								</div>
								<div className="flex flex-col gap-0.5">
									{section.items.map(item => (
										<SectionItem key={item.label} {...item} isCollapsed={isCollapsed} />
									))}
								</div>
							</section>
						))}
					</div>
				</nav>
				<nav
					aria-label="Account"
					className="border-component-border expanded:px-3 shrink-0 space-y-0.5 border-t px-2 py-3"
				>
					{accountItems.map(item => (
						<SectionItem key={item.label} {...item} isCollapsed={isCollapsed} />
					))}
				</nav>
			</aside>
		</div>
	)
}

const SectionItem: FC<SectionItem & { isCollapsed: boolean }> = ({
	label,
	Icon,
	onClick,
	activeUrl,
	exactActive,
	newFeature,
	hasImportantNotice,
	notificationCount,
	isCollapsed,
}) => {
	const location = useLocation()
	const url = activeUrl ?? (typeof onClick === "string" ? onClick : undefined)

	const isActive = url !== undefined && (exactActive ? location.pathname === url : location.pathname.startsWith(url))

	const buttonClass = cn(
		"group focus-visible:outline-primary expanded:justify-start relative flex min-h-9 w-full items-center justify-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2",
		isActive
			? "bg-primary/8 text-primary before:bg-primary font-semibold before:absolute before:inset-y-2.5 before:left-0 before:w-0.5 before:rounded-full"
			: "text-slate-600 hover:bg-black/4 hover:text-slate-950",
	)

	const content = (
		<>
			<Icon
				className={cn(
					"size-[18px] shrink-0 transition-colors duration-150",
					isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-600",
				)}
			/>
			<span className="expanded:not-sr-only expanded:flex-1 expanded:truncate expanded:text-start sr-only">
				{label}
			</span>
			{newFeature && (
				<span className="expanded:block hidden shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[10px] leading-4 font-semibold text-emerald-700 ring-1 ring-emerald-600/10 ring-inset">
					New
				</span>
			)}
			{hasImportantNotice && (
				<span className="bg-danger expanded:static absolute top-0.5 right-0.5 size-4 shrink-0 rounded-full p-0.5">
					<IconExclamationMark className="size-full text-white" />
					<span className="sr-only">Requires attention</span>
				</span>
			)}
			{notificationCount !== undefined && notificationCount > 0 && (
				<span className="bg-danger expanded:static absolute top-0.5 right-0.5 flex min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] leading-4 font-semibold text-white">
					{notificationCount > 99 ? "99+" : notificationCount}
				</span>
			)}
		</>
	)

	return (
		<div key={label} className="relative" {...getCollapsedTooltipProps(isCollapsed, label)}>
			{typeof onClick === "function" ? (
				<button type="button" onClick={onClick} className={cn(buttonClass, "cursor-pointer")}>
					{content}
				</button>
			) : (
				<CustomLink to={onClick} className={buttonClass} aria-current={isActive ? "page" : undefined}>
					{content}
				</CustomLink>
			)}
		</div>
	)
}

export default memo(Sidebar)
